'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import {
  ArrowRight,
  Check,
  Link2,
  Mail,
  MessageCircle,
  Send,
  Share2,
} from 'lucide-react'
import { trackActivity } from '~/lib/activity-tracking'
import { env } from '~/env'

const FONT_UI = 'font-[family-name:var(--font-rounded)]'
const FONT_HEADING = 'font-[family-name:var(--font-sora)]'
const FONT_BODY = 'font-[family-name:var(--font-nunito)]'

// Grain overlay matching the Path Collective card texture (feTurbulence noise)
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='cg'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23cg)'/%3E%3C/svg%3E\")"

type TicketState = 'AVAILABLE' | 'NO_ACTIVE_RELEASE' | 'EVENT_SOLD_OUT'

type EventRsvpCardProps = {
  slug: string
  eventId: string
  eventTitle: string
  isBooked: boolean
  isGuestBooked?: boolean
  ticketState?: TicketState
  isPastEvent?: boolean
  /** "Free", "$45", "From $20", "Donation" */
  priceLabel: string
  /** #1477: "deposit $X" when the cheapest paid tier is a deposit ticket. */
  depositNote?: string | null
  /** "7 of 30" when the event is capped, null when uncapped */
  spotsLabel: string | null
  formatLabel: string
  accessToken?: string
}

export default function EventRsvpCard({
  slug,
  eventId,
  eventTitle,
  isBooked,
  isGuestBooked = false,
  ticketState,
  isPastEvent = false,
  priceLabel,
  depositNote = null,
  spotsLabel,
  formatLabel,
  accessToken,
}: EventRsvpCardProps) {
  const [shareOpen, setShareOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  // Server call sites only know the relative path; absolutize at render time
  // (same convention as EventShareButton / ShareQR).
  const [origin, setOrigin] = useState('')
  useEffect(() => {
    setOrigin(window.location.origin)
  }, [])
  const eventUrl = `${origin || env.NEXT_PUBLIC_WEB_URL || ''}/events/${eventId}`
  const shareText = `${eventTitle} — ${eventUrl}`

  const booked = isBooked || isGuestBooked
  const soldOut = ticketState === 'EVENT_SOLD_OUT'
  const isFree = priceLabel === 'Free' || priceLabel === 'Donation'

  const eyebrow = isPastEvent
    ? 'Ended'
    : booked
      ? "You're in"
      : soldOut
        ? 'Sold out'
        : 'Open'

  const note = isPastEvent
    ? 'This gathering has already taken place.'
    : booked
      ? "You're on the list — see you there"
      : soldOut
        ? 'No spots remaining for this gathering'
        : spotsLabel
          ? 'Limited capacity · confirm to enter'
          : 'Confirm to enter'

  const toggleShare = () => {
    const opening = !shareOpen
    setShareOpen(opening)
    if (opening) {
      trackActivity('EVENT_SHARE', 'EVENT', eventId, { accessToken, skipDedup: true })
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    } catch {
      // Clipboard unavailable (permissions) — nothing to revert
    }
  }

  const shareOptions = [
    {
      key: 'copy',
      label: copied ? 'Copied!' : 'Copy link',
      icon: copied ? Check : Link2,
      onClick: handleCopy,
    },
    {
      key: 'messages',
      label: 'Messages',
      icon: MessageCircle,
      href: `sms:?&body=${encodeURIComponent(shareText)}`,
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: Send,
      href: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
    },
    {
      key: 'email',
      label: 'Email',
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(eventTitle)}&body=${encodeURIComponent(shareText)}`,
    },
  ]

  const primaryDisabled = isPastEvent || booked || soldOut
  const primaryLabel = isPastEvent
    ? 'Booking closed'
    : booked
      ? 'Booked ✓'
      : soldOut
        ? 'Sold out'
        : isFree
          ? 'Reserve seat'
          : 'Get tickets'

  return (
    <aside className="relative overflow-hidden rounded-[20px] bg-[#75A096] p-6 sm:p-7 shadow-[0_14px_36px_rgba(78,110,101,0.30)]">
      {/* Grain texture overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30 mix-blend-soft-light"
        style={{ backgroundImage: GRAIN, backgroundSize: '200px 200px' }}
      />
      <div className="relative z-10">
        <p className={`${FONT_BODY} text-[11px] font-bold uppercase tracking-[0.2em] text-[#F7F0E6]/90`}>
          {eyebrow} · {priceLabel}
        </p>
        <h2 className={`${FONT_HEADING} mt-2 mb-1.5 text-[32px] font-extrabold uppercase leading-none tracking-[0.03em] text-[#F7F0E6]`}>
          RSVP
        </h2>
        <p className={`${FONT_BODY} mb-5 text-[13px] text-[#F7F0E6]/80`}>{note}</p>

        {/* Primary CTA */}
        {primaryDisabled ? (
          <button
            disabled
            className={`${FONT_UI} mb-3 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full p-3.5 text-[15px] font-bold ${
              booked
                ? 'bg-[#F7F0E6] text-[#3F6A5E]'
                : 'bg-[#F7F0E6]/30 text-[#F7F0E6]/70'
            }`}
          >
            {primaryLabel}
          </button>
        ) : (
          <Link
            href={`/events/${slug}/tickets`}
            className={`${FONT_UI} group mb-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#F0B853] p-3.5 text-[15px] font-bold text-[#2C2D2B] shadow-[inset_0_0_0_1.5px_#D99E37] transition-all hover:-translate-y-px hover:shadow-[inset_0_0_0_1.5px_#D99E37,0_8px_20px_rgba(217,158,55,0.34)]`}
          >
            {primaryLabel}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-[3px]" />
          </Link>
        )}

        {/* #802 — a member who already holds paid/donation tickets can buy
            more (each purchase is its own booking); the checkout confirms
            "you already have N — add more?" before charging. Free RSVPs stay
            one per member, and guests are unchanged. */}
        {isBooked && !isGuestBooked && !isPastEvent && !soldOut && priceLabel !== 'Free' && (
          <Link
            href={`/events/${slug}/tickets?repeat=1`}
            data-testid="book-more-tickets"
            className={`${FONT_UI} group mb-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#F0B853] p-3.5 text-[15px] font-bold text-[#2C2D2B] shadow-[inset_0_0_0_1.5px_#D99E37] transition-all hover:-translate-y-px`}
          >
            Book more tickets
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-[3px]" />
          </Link>
        )}

        {/* Share drop — ghost button toggling the share menu */}
        <button
          type="button"
          onClick={toggleShare}
          aria-expanded={shareOpen}
          className={`${FONT_UI} flex w-full items-center justify-center gap-2 rounded-full bg-transparent p-3.5 text-[15px] font-bold text-[#F7F0E6] shadow-[inset_0_0_0_1.5px_rgba(247,240,230,0.6)] transition-colors hover:bg-[#F7F0E6]/10 hover:shadow-[inset_0_0_0_1.5px_#F7F0E6]`}
        >
          <Share2 className="h-[15px] w-[15px]" />
          Share drop
        </button>

        {shareOpen && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {shareOptions.map((opt) => {
              const Icon = opt.icon
              const cls = `${FONT_UI} flex items-center gap-1.5 rounded-xl border border-[#F7F0E6]/30 bg-[#F7F0E6]/15 px-3 py-2.5 text-[12.5px] font-bold text-[#F7F0E6] transition-colors hover:bg-[#F7F0E6]/25`
              return opt.href ? (
                <a key={opt.key} href={opt.href} target="_blank" rel="noopener noreferrer" className={cls}>
                  <Icon className="h-[15px] w-[15px] flex-none" />
                  {opt.label}
                </a>
              ) : (
                <button key={opt.key} type="button" onClick={opt.onClick} className={cls}>
                  <Icon className="h-[15px] w-[15px] flex-none" />
                  {opt.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Meta lines */}
        <div className="mt-5 flex flex-col gap-2.5 border-t border-[#F7F0E6]/20 pt-4">
          <div className={`${FONT_BODY} flex justify-between text-[13px] text-[#F7F0E6]/80`}>
            <span>Price</span>
            <b className="font-bold text-[#F7F0E6]">{priceLabel}</b>
          </div>
          {depositNote && (
            <p className={`${FONT_BODY} -mt-1.5 text-right text-[12px] text-[#F7F0E6]/70`}>
              {depositNote} today, balance paid to the facilitator
            </p>
          )}
          {spotsLabel && (
            <div className={`${FONT_BODY} flex justify-between text-[13px] text-[#F7F0E6]/80`}>
              <span>Spots left</span>
              <b className="font-bold text-[#F7F0E6]">{spotsLabel}</b>
            </div>
          )}
          <div className={`${FONT_BODY} flex justify-between text-[13px] text-[#F7F0E6]/80`}>
            <span>Format</span>
            <b className="font-bold text-[#F7F0E6]">{formatLabel}</b>
          </div>
        </div>
      </div>
    </aside>
  )
}
